
import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  Alert,
  ActivityIndicator,
} from 'react-native';
import { useRouter } from 'expo-router';
import { useAuth } from '@/contexts/AuthContext';
import { MagicLinkService } from '@/services/magicLinkService';
import AsyncStorage from '@react-native-async-storage/async-storage';
import PrimaryButton from '@/components/PrimaryButton';
import SecondaryButton from '@/components/SecondaryButton';

export default function PatientWelcomeScreen() {
  const router = useRouter();
  const { user, userProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [isPatient, setIsPatient] = useState(false);
  const [needsPassword, setNeedsPassword] = useState(false);

  useEffect(() => {
    const initializeScreen = async () => {
      // Clean up any validation data since we're now in the welcome flow
      try {
        await AsyncStorage.removeItem('validated_user_info');
      } catch (error) {
        console.log('⚠️ Could not clean up validation data:', error);
      }

      checkPatientStatus();
    };

    initializeScreen();
  }, [user]);

  const checkPatientStatus = async () => {
    if (!user) {
      router.replace('/login');
      return;
    }

    try {
      // Check if user is from patient invitation
      const patientStatus = await MagicLinkService.isPatientInvite(user.id);
      setIsPatient(patientStatus);

      // Check if user needs to set password
      const passwordNeeded = await MagicLinkService.needsPasswordSetup(user.id);
      setNeedsPassword(passwordNeeded);

      // If they need a password, redirect them
      if (passwordNeeded) {
        Alert.alert(
          'Configuração Necessária',
          'Você precisa definir uma senha antes de continuar.',
          [
            {
              text: 'Definir Senha',
              onPress: () => router.replace('/set-password')
            }
          ]
        );
      }
    } catch (error) {
      console.error('❌ Error checking patient status:', error);
    }
  };

  const handleStartJourney = () => {
    if (needsPassword) {
      router.push('/set-password');
    } else {
      router.replace('/sobriety-clock');
    }
  };

  const handleSetPassword = () => {
    router.push('/change-password');
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <View style={[styles.container, styles.loadingContainer]}>
          <ActivityIndicator size="large" color="#015480" />
          <Text style={styles.loadingText}>Carregando...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.safeArea}>
      <View style={styles.container}>
        <View style={styles.content}>
          <Text style={styles.title}>
            Bem-vindo ao Padrinho{userProfile?.full_name ? `, ${userProfile.full_name}` : ''}!
          </Text>

          <Text style={styles.subtitle}>
            {isPatient
              ? 'Você foi convidado por sua equipe de cuidados para iniciar sua jornada de recuperação. Nossa plataforma está aqui para te apoiar em cada passo.'
              : 'Você foi convidado para iniciar sua jornada de recuperação. Nossa equipe está aqui para te apoiar em cada passo.'
            }
          </Text>

          <View style={styles.features}>
            <Text style={styles.featuresTitle}>O que você encontrará:</Text>
            
            <View style={styles.feature}>
              <Text style={styles.featureText}>🤖 Conversar com Bill, seu padrinho digital</Text>
            </View>
            
            <View style={styles.feature}>
              <Text style={styles.featureText}>⏰ Acompanhar sua jornada de sobriedade</Text>
            </View>
            
            <View style={styles.feature}>
              <Text style={styles.featureText}>📔 Registrar seus sentimentos e progressos</Text>
            </View>
            
            <View style={styles.feature}>
              <Text style={styles.featureText}>🎯 Participar de reuniões e encontros</Text>
            </View>
          </View>
        </View>

        <View style={styles.bottomSection}>
          {needsPassword && (
            <SecondaryButton
              title="Definir Senha"
              onPress={handleSetPassword}
              style={styles.passwordButton}
            />
          )}
          
          <PrimaryButton
            title={needsPassword ? 'Continuar sem senha' : 'Iniciar Jornada'}
            onPress={handleStartJourney}
          />
        </View>
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  container: {
    flex: 1,
    paddingHorizontal: 24,
  },
  loadingContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    gap: 16,
  },
  loadingText: {
    fontSize: 16,
    fontFamily: 'Inter_400Regular',
    color: '#666666',
  },
  content: {
    flex: 1,
    paddingTop: 40,
    gap: 32,
  },
  title: {
    fontSize: 28,
    fontFamily: 'Sentient-Regular',
    color: '#151515',
    lineHeight: 34,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 16,
    fontFamily: 'Inter_400Regular',
    color: '#666666',
    lineHeight: 24,
    textAlign: 'center',
  },
  features: {
    gap: 16,
  },
  featuresTitle: {
    fontSize: 18,
    fontFamily: 'Inter',
    fontWeight: '600',
    color: '#151515',
    marginBottom: 8,
  },
  feature: {
    paddingVertical: 8,
  },
  featureText: {
    fontSize: 16,
    fontFamily: 'Inter_400Regular',
    color: '#333333',
    lineHeight: 22,
  },
  bottomSection: {
    paddingVertical: 32,
    gap: 16,
  },
  passwordButton: {
    marginBottom: 8,
  },
});
